import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { clientsAPI, clientGroupsAPI } from '../services/api';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Tooltip,
  Grid,
  Stack,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Block as DisableIcon,
  CheckCircle as EnableIcon,
  FileDownload as ExportIcon,
  FileUpload as ImportIcon,
} from '@mui/icons-material';

const Clients = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [clients, setClients] = useState([]);
  const [clientGroups, setClientGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [filters, setFilters] = useState({ clientNumber: '', name: '', clientRef: '', contactName: '', status: 'all' });
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    companyName: '',
    clientReference: '',
    contactName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    clientGroupId: '',
    discount: 0,
    enabled: true,
    username: '',
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [clientsRes, groupsRes] = await Promise.all([
        clientsAPI.getAll(),
        clientGroupsAPI.getAll(),
      ]);
      setClients(clientsRes.data);
      setClientGroups(groupsRes.data);
      setError('');
    } catch (err) {
      setError(t('clients.failedToLoad'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenModal = (client = null) => {
    if (client) {
      setEditingClient(client);
      setFormData({
        companyName: client.companyName,
        clientReference: client.clientReference || '',
        contactName: client.contactName || '',
        email: client.email || '',
        phone: client.phone || '',
        address: client.address || '',
        city: client.city || '',
        clientGroupId: client.clientGroupId || '',
        discount: client.discount ?? 0,
        enabled: client.enabled,
        username: client.user?.username || '',
      });
    } else {
      setEditingClient(null);
      setFormData({
        companyName: '', clientReference: '', contactName: '', email: '',
        phone: '', address: '', city: '', clientGroupId: '', discount: 0, enabled: true, username: '',
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingClient(null);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData({ ...formData, [name]: name === 'enabled' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        const { companyName, contactName, email, phone, address, city, clientGroupId, discount } = formData;
        await clientsAPI.update(editingClient.id, { companyName, contactName, email, phone, address, city, clientGroupId, discount });
      } else {
        await clientsAPI.create(formData);
      }
      handleCloseModal();
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || t('clients.failedToSave'));
    }
  };

  const handleToggleStatus = async (client) => {
    try {
      if (client.enabled) {
        await clientsAPI.disable(client.id);
      } else {
        await clientsAPI.enable(client.id);
      }
      fetchData();
    } catch (err) {
      setError(t('clients.failedToUpdateStatus'));
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const response = await clientsAPI.exportCsv();
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `clientes-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(t('clients.failedToExport'));
    } finally {
      setExporting(false);
    }
  };

  const handleImportFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        setImporting(true);
        const response = await clientsAPI.importCsv(ev.target.result);
        setImportResult(response.data);
        fetchData();
      } catch (err) {
        setError(err.response?.data?.message || t('clients.failedToImport'));
      } finally {
        setImporting(false);
      }
    };
    reader.readAsText(file, 'UTF-8');
  };

  const matchesText = (val, q) => (val || '').toString().toLowerCase().includes(q.trim().toLowerCase());
  const filteredClients = clients.filter((c) =>
    matchesText(c.clientNumber, filters.clientNumber) &&
    matchesText(c.companyName, filters.name) &&
    matchesText(c.clientReference, filters.clientRef) &&
    matchesText(c.contactName, filters.contactName) &&
    (filters.status === 'all' || (filters.status === 'enabled' ? c.enabled : !c.enabled))
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={1}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>{t('clients.title')}</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button variant="outlined"
            startIcon={exporting ? <CircularProgress size={16} /> : <ExportIcon />}
            onClick={handleExport} disabled={exporting}>
            {exporting ? t('clients.exporting') : t('clients.exportCsv')}
          </Button>
          <Button variant="outlined"
            startIcon={importing ? <CircularProgress size={16} /> : <ImportIcon />}
            onClick={() => fileInputRef.current?.click()} disabled={importing}>
            {importing ? t('clients.importing') : t('clients.importCsv')}
          </Button>
          <input type="file" accept=".csv,text/csv" ref={fileInputRef}
            style={{ display: 'none' }} onChange={handleImportFile} />
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenModal()}>
            {t('clients.addClient')}
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 600 }}>{t('clients.clientNumber')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('clients.companyName')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('clients.clientReference')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('clients.contactName')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('clients.clientGroup')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">{t('clients.discount')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('clients.username')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('clients.status')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('clients.actions')}</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ pt: 0 }}>
                <TextField fullWidth size="small" variant="standard" placeholder={t('common.filter')}
                  value={filters.clientNumber} onChange={(e) => setFilters({ ...filters, clientNumber: e.target.value })} />
              </TableCell>
              <TableCell sx={{ pt: 0 }}>
                <TextField fullWidth size="small" variant="standard" placeholder={t('common.filter')}
                  value={filters.name} onChange={(e) => setFilters({ ...filters, name: e.target.value })} />
              </TableCell>
              <TableCell sx={{ pt: 0 }}>
                <TextField fullWidth size="small" variant="standard" placeholder={t('common.filter')}
                  value={filters.clientRef} onChange={(e) => setFilters({ ...filters, clientRef: e.target.value })} />
              </TableCell>
              <TableCell sx={{ pt: 0 }}>
                <TextField fullWidth size="small" variant="standard" placeholder={t('common.filter')}
                  value={filters.contactName} onChange={(e) => setFilters({ ...filters, contactName: e.target.value })} />
              </TableCell>
              <TableCell />
              <TableCell />
              <TableCell />
              <TableCell sx={{ pt: 0 }}>
                <TextField select fullWidth size="small" variant="standard"
                  value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                  <MenuItem value="all">{t('common.all')}</MenuItem>
                  <MenuItem value="enabled">{t('common.enabled')}</MenuItem>
                  <MenuItem value="disabled">{t('common.disabled')}</MenuItem>
                </TextField>
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredClients.map((client) => (
              <TableRow key={client.id}>
                <TableCell>{client.clientNumber}</TableCell>
                <TableCell>{client.companyName}</TableCell>
                <TableCell>{client.clientReference}</TableCell>
                <TableCell>
                  <Typography variant="body2">{client.contactName}</Typography>
                  <Typography variant="caption" color="text.secondary">{client.phone}</Typography>
                </TableCell>
                <TableCell>{client.clientGroup?.name || '-'}</TableCell>
                <TableCell align="right">{parseFloat(client.discount) > 0 ? `${parseFloat(client.discount)}%` : '-'}</TableCell>
                <TableCell>{client.user?.username || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={client.enabled ? t('common.enabled') : t('common.disabled')}
                    color={client.enabled ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title={t('common.edit')}>
                    <IconButton onClick={() => handleOpenModal(client)} size="small" aria-label={t('common.edit')}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={client.enabled ? t('common.disable') : t('common.enable')}>
                    <IconButton
                      onClick={() => handleToggleStatus(client)}
                      size="small"
                      color={client.enabled ? 'error' : 'success'}
                    >
                      {client.enabled ? <DisableIcon /> : <EnableIcon />}
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {filteredClients.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  {t('clients.noClientsMatch')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit/Create Modal */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth fullScreen={fullScreen}>
        <DialogTitle>
          {editingClient ? t('clients.editClient') : t('clients.addNew')}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={t('clients.companyName')} name="companyName"
                  value={formData.companyName} onChange={handleChange} required margin="normal" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={t('clients.clientReference')} name="clientReference"
                  value={formData.clientReference} onChange={handleChange} margin="normal" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={t('clients.contactName')} name="contactName"
                  value={formData.contactName} onChange={handleChange} margin="normal" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={t('clients.email')} name="email" type="email"
                  value={formData.email} onChange={handleChange} margin="normal" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={t('clients.phone')} name="phone"
                  value={formData.phone} onChange={handleChange} margin="normal" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={t('clients.city')} name="city"
                  value={formData.city} onChange={handleChange} margin="normal" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label={t('clients.address')} name="address"
                  value={formData.address} onChange={handleChange} margin="normal" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel required>{t('clients.clientGroup')}</InputLabel>
                  <Select name="clientGroupId" value={formData.clientGroupId}
                    onChange={handleChange} label={t('clients.clientGroup')}>
                    {clientGroups.map((group) => (
                      <MenuItem key={group.id} value={group.id}>{group.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={t('clients.discount')} name="discount" type="number"
                  value={formData.discount} onChange={handleChange} margin="normal"
                  inputProps={{ min: 0, max: 100, step: 0.5 }}
                  helperText={t('clients.discountHint')}
                  InputProps={{ endAdornment: <Typography color="text.secondary">%</Typography> }} />
              </Grid>

              {/* Username field — only for new client creation */}
              {!editingClient && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('clients.username')}
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    required
                    margin="normal"
                    helperText={t('clients.usernameHint')}
                  />
                </Grid>
              )}

              {/* When editing, show linked username as read-only */}
              {editingClient && formData.username && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('clients.linkedUser')}
                    value={formData.username}
                    margin="normal"
                    disabled
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch name="enabled" checked={formData.enabled} onChange={handleChange} />}
                  label={t('common.enabled')}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal}>{t('common.cancel')}</Button>
            <Button type="submit" variant="contained">
              {editingClient ? t('common.update') : t('common.create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Import Result Dialog */}
      <Dialog open={!!importResult} onClose={() => setImportResult(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('clients.importResults')}</DialogTitle>
        <DialogContent>
          {importResult && (
            <Box>
              <Alert severity={importResult.errors?.length ? 'warning' : 'success'} sx={{ mb: 2 }}>
                {t('clients.importSummary', {
                  created: importResult.created,
                  updated: importResult.updated,
                  errors: importResult.errors?.length ?? 0,
                })}
              </Alert>
              {importResult.errors?.length > 0 && (
                <List dense>
                  {importResult.errors.map((e, i) => (
                    <ListItem key={i}><ListItemText primary={e} /></ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportResult(null)} variant="contained">{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Clients;
